import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL !== undefined
  ? process.env.REACT_APP_API_URL
  : 'https://cinema-back-kjkx.onrender.com';

const api = axios.create({
  baseURL: `${API_BASE}/api`,
  timeout: 10000,
});

api.interceptors.request.use(config => {
  const token = localStorage.getItem('kup-token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const authApi = axios.create({
  baseURL: `${API_BASE}/api/auth`,
  timeout: 10000,
});

export const fetchDashboardSummary = () => api.get('/dashboard/summary').then(r => r.data);
export const fetchInsights = () => api.get('/analytics/insights').then(r => r.data);

export const fetchWeekendArchive = (year) =>
  api.get(`/analytics/weekend-archive${year ? `?year=${year}` : ''}`).then(r => r.data);

// Cinema events (ימי קולנוע ואירועים מיוחדים)
export const fetchEvents = () => api.get('/events').then(r => r.data);
export const fetchEventSuggestions = () => api.get('/admin/events/suggestions').then(r => r.data);
export const createEvent = (body) => api.post('/admin/events', body).then(r => r.data);
export const updateEvent = (id, body) => api.put(`/admin/events/${id}`, body).then(r => r.data);
export const deleteEvent = (id) => api.delete(`/admin/events/${id}`).then(r => r.data);
export const fetchMovies = (startDate, endDate, includeEmpty = false) => {
  const q = new URLSearchParams();
  if (startDate) q.set('start_date', startDate);
  if (endDate) q.set('end_date', endDate);
  if (includeEmpty) q.set('include_empty', 'true');
  const qs = q.toString();
  return api.get(`/movies${qs ? `?${qs}` : ''}`, { timeout: 30000 }).then(r => r.data);
};
export const fetchMovieDetail = (id, startDate, endDate) => {
  const q = new URLSearchParams();
  if (startDate) q.set('start_date', startDate);
  if (endDate) q.set('end_date', endDate);
  const qs = q.toString();
  return api.get(`/movies/${id}${qs ? `?${qs}` : ''}`).then(r => r.data);
};
export const updateManualScreeningCount = (screeningId, ticketsSold) =>
  api.put(`/movies/screenings/${screeningId}/manual-count`, { tickets_sold: ticketsSold }).then(r => r.data);
export const triggerScreeningRescan = (screeningId) =>
  api.post(`/scrape/screening/${screeningId}/rescan`, null, { timeout: 60000 }).then(r => r.data);
export const fetchCinemas = () => api.get('/cinemas').then(r => r.data);
// Branches page (סניפים): every figure is over a range, default the weekend.
const rangeQs = (start, end, extra = {}) => {
  const q = new URLSearchParams(extra);
  if (start && end) { q.set('start_date', start); q.set('end_date', end); }
  return q.toString();
};
export const fetchVenues = (start, end) =>
  api.get(`/analytics/venues?${rangeQs(start, end)}`, { timeout: 30000 }).then(r => r.data);
export const fetchVenueTotals = () =>
  api.get('/analytics/venues/totals', { timeout: 30000 }).then(r => r.data);
export const fetchCinemaDetail = (cinemaId, start, end, baseline = 'market') =>
  api.get(`/analytics/cinema/${cinemaId}?${rangeQs(start, end, { baseline })}`, { timeout: 30000 }).then(r => r.data);
export const fetchCinemaScreenings = (cinemaId, when, start, end, offset = 0, limit = 50) =>
  api.get(`/cinemas/${cinemaId}/screenings?${rangeQs(start, end, { when, offset, limit })}`).then(r => r.data);
export const fetchTicketsByDate = (days = 14) => api.get(`/analytics/tickets-by-date?days=${days}`).then(r => r.data);
export const fetchTicketsByBranch = () => api.get('/analytics/tickets-by-branch').then(r => r.data);
export const fetchTopMovies = (limit = 10) => api.get(`/analytics/top-movies?limit=${limit}`).then(r => r.data);
export const fetchOccupancyByFormat = () => api.get('/analytics/occupancy-by-format').then(r => r.data);
export const fetchTicketsByHour = () => api.get('/analytics/tickets-by-hour').then(r => r.data);
export const fetchOccupancyByDayOfWeek = () => api.get('/analytics/occupancy-by-day-of-week').then(r => r.data);
export const fetchMovieTrends = () => api.get('/analytics/movie-trends').then(r => r.data);
export const fetchDeadScreenings = (threshold = 10) => api.get(`/analytics/dead-screenings?threshold=${threshold}`).then(r => r.data);
export const fetchFormatByBranch = () => api.get('/analytics/format-by-branch').then(r => r.data);
export const fetchBranchEfficiency = () => api.get('/analytics/branch-efficiency').then(r => r.data);
export const fetchGenreStats = () => api.get('/analytics/genre-stats').then(r => r.data);
export const fetchScrapeLogs = () => api.get('/scrape-logs').then(r => r.data);
export const fetchScheduleToday = () => api.get('/schedule/today').then(r => r.data);
export const fetchLevMissingSeatplans = () => api.get('/lev/missing-seatplans').then(r => r.data);
export const triggerScrape = (chain) => api.post(`/scrape/trigger${chain ? `?chain=${chain}` : ''}`).then(r => r.data);
export const triggerTicketScan = (chain) => api.post(`/scrape/tickets${chain ? `?chain=${chain}` : ''}`).then(r => r.data);
export const cancelAllScrapes = () => api.post('/scrape/cancel').then(r => r.data);

export const checkHealth = () => api.get('/health', { timeout: 15000 })
  .then(() => ({ ok: true }))
  .catch(() => ({ ok: false }));

export const getDebugScreenshotUrl = () => `${API_BASE}/api/debug-screenshot`;
export const getDebugScreenshotTicketsUrl = () => `${API_BASE}/api/debug-screenshot-tickets`;

export const fetchBlockedSeatsStats = () => api.get('/analytics/blocked-seats').then(r => r.data);
export const fetchBlockedSeatBreakdown = (cinemaId, hall) =>
  api.get(`/analytics/blocked-seats/${cinemaId}/${encodeURIComponent(hall)}`).then(r => r.data);
export const generateBlockedSeatDebugPack = (cinemaId, hall) =>
  api.get(`/analytics/blocked-seats/${cinemaId}/${encodeURIComponent(hall)}/debug-pack`, { timeout: 30000 }).then(r => r.data);
export const resetHallBlockedSeats = (cinemaId, hall) =>
  api.delete(`/admin/blocked-seats/${encodeURIComponent(cinemaId)}/${encodeURIComponent(hall)}`).then(r => r.data);
export const fetchDebugScreenshots = (params = {}) => {
  const q = new URLSearchParams();
  if (params.chain) q.set('chain', params.chain);
  if (params.step) q.set('step', params.step);
  if (params.limit) q.set('limit', params.limit);
  if (params.since) q.set('since', params.since);
  const qs = q.toString();
  return api.get(`/debug-screenshots${qs ? `?${qs}` : ''}`).then(r => r.data);
};
export const getDebugScreenshotFileUrl = (filename) => `${API_BASE}/api/debug-screenshots/${filename}`;
export const clearDebugScreenshots = () => api.delete('/debug-screenshots').then(r => r.data);

// Per-scraper debug page (backed by /api/debug/*)
export const fetchDebugStatus = (chainKey) =>
  api.get(`/debug/status?chain=${encodeURIComponent(chainKey)}`).then(r => r.data);
export const fetchDebugScreenings = (chainKey, { hours = 36, flag = 'all', limit = 500 } = {}) =>
  api.get(`/debug/screenings?chain=${encodeURIComponent(chainKey)}&hours=${hours}&flag=${flag}&limit=${limit}`)
    .then(r => r.data);
export const fetchFilteredScreenings = (chainKey) =>
  api.get(`/debug/filtered-screenings?chain=${encodeURIComponent(chainKey)}`).then(r => r.data);
export const forceAddScreening = (chainKey, screening) =>
  api.post('/debug/filtered-screenings/force-add', { chain: chainKey, ...screening }).then(r => r.data);

export const fetchDebugLogs = (chainKey, { limit = 500, search = '', since = '' } = {}) => {
  const q = new URLSearchParams({ chain: chainKey, limit: String(limit) });
  if (search) q.set('search', search);
  if (since) q.set('since', since);
  return api.get(`/debug/logs?${q.toString()}`, { timeout: 30000 }).then(r => r.data);
};

// Admin - Allowed Movies
export const fetchAllowedMovies = () => api.get('/admin/allowed-movies').then(r => r.data);
export const addAllowedMovie = (title) => api.post(`/admin/allowed-movies?title=${encodeURIComponent(title)}`).then(r => r.data);
export const updateAllowedMovie = (id, isActive) => api.put(`/admin/allowed-movies/${id}?is_active=${isActive}`).then(r => r.data);
export const deleteAllowedMovie = (id) => api.delete(`/admin/allowed-movies/${id}`).then(r => r.data);
export const fetchUnmatchedTitles = () => api.get('/admin/unmatched-titles').then(r => r.data);
export const fetchAdminHalls = () => api.get('/admin/halls').then(r => r.data);
export const setAdminHallManualSeats = (cinemaId, hall, totalSeats) =>
  api.put(`/admin/halls/${cinemaId}/${encodeURIComponent(hall)}/manual-seats`, { total_seats: totalSeats }).then(r => r.data);

// TMDB free-text search
export const searchTmdbByText = (q, limit = 10) =>
  api.get(`/admin/tmdb/text-search?q=${encodeURIComponent(q)}&limit=${limit}`, { timeout: 30000 }).then(r => r.data);

// Admin - TMDB Settings
export const fetchTmdbSettings = () => api.get('/admin/settings/tmdb').then(r => r.data);
export const saveTmdbApiKey = (key) => api.post('/admin/settings/tmdb', { api_key: key }).then(r => r.data);
export const enrichAllMovies = () => api.post('/admin/tmdb/enrich-all', null, { timeout: 120000 }).then(r => r.data);
export const backfillTitleEn = () => api.post('/admin/tmdb/backfill-title-en', null, { timeout: 120000 }).then(r => r.data);
export const enrichSingleMovie = (movieId) => api.post(`/admin/tmdb/enrich/${movieId}`, null, { timeout: 30000 }).then(r => r.data);
// Credits/budget for movies already matched to TMDB — enrichAllMovies only
// looks at unmatched ones, so it can never fill these for the existing catalogue.
export const backfillTmdbCredits = (limit = 300) =>
  api.post(`/admin/tmdb/backfill-credits?limit=${limit}`, null, { timeout: 300000 }).then(r => r.data);

// Admin - Prediction data foundation (capture layer; no model)
export const fetchPredictionDataStats = () => api.get('/admin/prediction-data/stats').then(r => r.data);
export const triggerBuzzCapture = () => api.post('/admin/buzz/capture', null, { timeout: 300000 }).then(r => r.data);
export const fetchRecentBuzz = (days = 14) => api.get(`/admin/buzz/recent?days=${days}`).then(r => r.data);
export const fetchBuzzSources = () => api.get('/admin/buzz/sources').then(r => r.data);
export const saveBuzzSources = (payload) => api.post('/admin/buzz/sources', payload).then(r => r.data);

// Admin - Blocked Seats
export const resetBlockedSeats = () => api.delete('/admin/blocked-seats').then(r => r.data);
export const resetAllScreenings = () => api.delete('/admin/screenings/all').then(r => r.data);
export const fetchPendingBlockedSeats = () => api.get('/admin/blocked-seats/pending').then(r => r.data);
export const fetchBlockedSeatEvents = (limit = 50) =>
  api.get('/admin/blocked-seats/events', { params: { limit } }).then(r => r.data);
export const manualBlockSeats = (cinemaId, hall, seatIds) =>
  api.post(`/admin/blocked-seats/${cinemaId}/${encodeURIComponent(hall)}/manual`, { seat_ids: seatIds }).then(r => r.data);
export const decidePendingBlockedSeats = (cinemaId, hall, decision) =>
  api.post(`/admin/blocked-seats/${cinemaId}/${encodeURIComponent(hall)}/decision`, { decision }).then(r => r.data);

// Admin - TMDB manual matching
export const fetchTmdbCandidates = (movieId) => api.get(`/admin/tmdb/search/${movieId}`, { timeout: 30000 }).then(r => r.data);
export const selectTmdbMatch = (movieId, tmdbId) => api.post(`/admin/tmdb/select/${movieId}?tmdb_id=${tmdbId}`, null, { timeout: 30000 }).then(r => r.data);
export const resetMovieTmdb = (movieId) => api.post(`/admin/tmdb/reset/${movieId}`).then(r => r.data);

// Admin - Movie canonical / variants
export const fetchMovieDuplicates = () => api.get('/admin/movies/duplicates').then(r => r.data);
export const setMovieCanonical = (id, canonicalId) =>
  api.post(`/admin/movies/${id}/canonical/${canonicalId}`).then(r => r.data);
export const clearMovieCanonical = (id) =>
  api.post(`/admin/movies/${id}/canonical/clear`).then(r => r.data);
export const mergeMoviesByTmdb = () => api.post('/admin/movies/merge-by-tmdb').then(r => r.data);
export const setMovieFiltered = (id, filtered) =>
  api.post(`/admin/movies/${id}/set-filtered?filtered=${filtered}`).then(r => r.data);

// Movie lifetime analytics
export const fetchMovieLifetime = (id) => api.get(`/analytics/movie-lifetime/${id}`).then(r => r.data);
export const fetchMovieBreakdown = (id) => api.get(`/analytics/movie-breakdown/${id}`).then(r => r.data);
export const fetchShowdown = (movieIds, align = 'day_number') =>
  api.get(`/analytics/showdown?movie_ids=${movieIds.join(',')}&align=${align}`).then(r => r.data);
// Rankings (§12.11): a category over a date range (no range = all time).
// params: { category, sub, dub_count, start, end }
export const fetchRankings = ({ category = 'all', sub, dub_count, start, end } = {}) => {
  const q = new URLSearchParams({ category });
  if (sub) q.set('sub', sub);
  if (dub_count) q.set('dub_count', dub_count);
  if (start && end) { q.set('start_date', start); q.set('end_date', end); }
  return api.get(`/analytics/rankings?${q}`, { timeout: 30000 }).then(r => r.data);
};

// This year / month / day and all time, each with its leading film
export const fetchRankingTotals = () => api.get('/analytics/rankings/totals', { timeout: 30000 }).then(r => r.data);

// Movie details a person decides (§12.11): an age rating and a distributor
// from the admin's lists, and Israeli. body: { age_rating_id, distributor_id, is_israeli }
export const updateMovieDetails = (movieId, body) =>
  api.put(`/admin/movies/${movieId}/details`, body).then(r => r.data);
// The two lists: { ages: [{id, name, min_age}], distributors: [{id, name}] }
export const fetchMovieOptions = () => api.get('/movie-options').then(r => r.data);
// The same with film counts, and the admin's edits to them
export const fetchMovieOptionsAdmin = () => api.get('/admin/movie-options').then(r => r.data);
export const addAgeRating = (name, minAge) => api.post('/admin/age-ratings', { name, min_age: minAge }).then(r => r.data);
export const updateAgeRating = (id, name, minAge) => api.put(`/admin/age-ratings/${id}`, { name, min_age: minAge }).then(r => r.data);
export const deleteAgeRating = (id) => api.delete(`/admin/age-ratings/${id}`).then(r => r.data);
export const addDistributor = (name) => api.post('/admin/distributors', { name }).then(r => r.data);
export const updateDistributor = (id, name) => api.put(`/admin/distributors/${id}`, { name }).then(r => r.data);
export const deleteDistributor = (id) => api.delete(`/admin/distributors/${id}`).then(r => r.data);

// Daily analytics (single day)
export const fetchDailyAnalytics = (date) => {
  const q = date ? `?date=${date}` : '';
  return api.get(`/analytics/daily${q}`).then(r => r.data);
};

// Weekend analytics (Thu–Sun)
export const fetchWeekendAnalytics = (startDate, endDate) => {
  const q = startDate && endDate ? `?start_date=${startDate}&end_date=${endDate}` : '';
  return api.get(`/analytics/weekend${q}`).then(r => r.data);
};

// Weekly analytics
export const fetchWeeklyAnalytics = (week, startDate, endDate) => {
  const q = new URLSearchParams();
  if (startDate && endDate) {
    q.set('start_date', startDate);
    q.set('end_date', endDate);
  } else if (week) {
    q.set('week', week);
  }
  const qs = q.toString();
  return api.get(`/analytics/weekly${qs ? `?${qs}` : ''}`).then(r => r.data);
};

// Cinema City hall code mappings
export const fetchCcHallMappings = () => api.get('/admin/cinema-city/hall-mappings').then(r => r.data);
export const upsertCcHallMapping = (code, label) =>
  api.post('/admin/cinema-city/hall-mappings', { code, label }).then(r => r.data);
export const deleteCcHallMapping = (code) =>
  api.delete(`/admin/cinema-city/hall-mappings/${encodeURIComponent(code)}`).then(r => r.data);

// Hall codes from DB (for mapping panel)
export const fetchHallCodes = (chain) =>
  api.get(`/admin/hall-codes?chain=${encodeURIComponent(chain)}`).then(r => r.data);

// Movieland hall code mappings
export const fetchMvlHallMappings = () => api.get('/admin/movieland/hall-mappings').then(r => r.data);
export const upsertMvlHallMapping = (code, label) =>
  api.post('/admin/movieland/hall-mappings', { code, label }).then(r => r.data);
export const deleteMvlHallMapping = (code) =>
  api.delete(`/admin/movieland/hall-mappings/${encodeURIComponent(code)}`).then(r => r.data);

// Lev hall seat-plan management
export const fetchLevHalls = () => api.get('/admin/lev/halls').then(r => r.data);
export const setLevHallSeatPlan = (venueId, seatplanId, rawJson) =>
  api.put(
    `/admin/lev/halls/${venueId}/${seatplanId}/plan`,
    { raw_json: rawJson },
    { timeout: 60000, maxBodyLength: Infinity, maxContentLength: Infinity },
  ).then(r => r.data);
export const clearLevHallSeatPlan = (venueId, seatplanId) =>
  api.delete(`/admin/lev/halls/${venueId}/${seatplanId}/plan`).then(r => r.data);

// Cinematheque TLV hall seat-plan management
export const fetchCinemathequeHalls = () => api.get('/admin/cinematheque/halls').then(r => r.data);
export const setCinemathequeHallSeatPlan = (venueId, seatplanId, rawJson) =>
  api.put(
    `/admin/cinematheque/halls/${venueId}/${seatplanId}/plan`,
    { raw_json: rawJson },
    { timeout: 60000, maxBodyLength: Infinity, maxContentLength: Infinity },
  ).then(r => r.data);
export const clearCinemathequeHallSeatPlan = (venueId, seatplanId) =>
  api.delete(`/admin/cinematheque/halls/${venueId}/${seatplanId}/plan`).then(r => r.data);

// Cinematheque JLM hall seat-plan management
export const fetchCinemathequeJlmHalls = () => api.get('/admin/cinematheque-jlm/halls').then(r => r.data);
export const setCinemathequeJlmHallSeatPlan = (venueId, seatplanId, rawJson) =>
  api.put(
    `/admin/cinematheque-jlm/halls/${venueId}/${seatplanId}/plan`,
    { raw_json: rawJson },
    { timeout: 60000, maxBodyLength: Infinity, maxContentLength: Infinity },
  ).then(r => r.data);
export const clearCinemathequeJlmHallSeatPlan = (venueId, seatplanId) =>
  api.delete(`/admin/cinematheque-jlm/halls/${venueId}/${seatplanId}/plan`).then(r => r.data);

// Decay probe
export const fetchDecayProbeCandidates = () => api.get('/admin/decay-probe/candidates').then(r => r.data);
export const createDecayProbe = (screeningId) =>
  api.post('/admin/decay-probe', { screening_id: screeningId }).then(r => r.data);
export const listDecayProbes = () => api.get('/admin/decay-probe').then(r => r.data);
export const getDecayProbeDetail = (probeId) =>
  api.get(`/admin/decay-probe/${probeId}`).then(r => r.data);
export const deleteDecayProbe = (probeId) =>
  api.delete(`/admin/decay-probe/${probeId}`).then(r => r.data);

export const fetchScreeningSnapshots = (screeningId) =>
  api.get(`/screenings/${screeningId}/snapshots`).then(r => r.data);

export default api;

// Predictions (experimental — no trained model yet)
export const fetchPredictionReadiness = () => api.get('/predictions/readiness').then(r => r.data);
export const triggerPredictionRun = () => api.post('/admin/predictions/run', null, { timeout: 300000 }).then(r => r.data);
export const fetchJobRuns = (params = {}) => api.get('/admin/job-runs', { params }).then(r => r.data);

// Instance mirroring — decisions replicate from the server, measurements never do
export const fetchSyncStatus = () => api.get('/admin/sync/status').then(r => r.data);
export const triggerCurationPull = (dryRun = false) =>
  api.post(`/admin/sync/pull?dry_run=${dryRun}`, null, { timeout: 180000 }).then(r => r.data);
export const fetchMirrorCompare = (days = 7) =>
  api.get(`/admin/mirror-compare?days=${days}`, { timeout: 120000 }).then(r => r.data);
export const fetchPredictionAccuracy = () => api.get('/predictions/accuracy').then(r => r.data);
export const fetchUpcomingPredictions = (limit = 40) =>
  api.get(`/predictions/upcoming?limit=${limit}`).then(r => r.data);
export const fetchAllocation = (daysAhead = 14, daysBack = 7) =>
  api.get(`/analytics/allocation?days_ahead=${daysAhead}&days_back=${daysBack}`).then(r => r.data);

// Resolved duplicate screenings (chain renamed a film / swapped a slot)
export const fetchScreeningConflicts = (limit = 50) =>
  api.get(`/admin/screening-conflicts?limit=${limit}`).then(r => r.data);
export const fetchScreeningRepoints = () =>
  api.get('/admin/screening-repoints', { timeout: 60000 }).then(r => r.data);
export const fetchImpossibleScreenings = () =>
  api.get('/admin/impossible-screenings', { timeout: 60000 }).then(r => r.data);
export const fetchScreeningGap = () =>
  api.get('/admin/screening-gap', { timeout: 60000 }).then(r => r.data);

// Branches a chain opened — adopted automatically, or still unmapped
export const fetchNewBranches = () =>
  api.get('/admin/new-branches', { timeout: 30000 }).then(r => r.data);
export const updateCinema = (cinemaId, body) =>
  api.put(`/admin/cinemas/${cinemaId}`, body).then(r => r.data);
export const probeBranchSiteCode = (theaterId) =>
  api.post(`/admin/new-branches/probe/${theaterId}`, null,
    { timeout: 120000 }).then(r => r.data);
export const scanChainBranches = () =>
  api.post('/admin/new-branches/scan', null, { timeout: 120000 }).then(r => r.data);
export const adoptBranchNow = (chainKey, theaterId) =>
  api.post(`/admin/new-branches/adopt/${chainKey}/${theaterId}`, null,
    { timeout: 120000 }).then(r => r.data);
export const probeHotCinemaEvents = (limit = 3) =>
  api.post(`/admin/hot-cinema-event-probe?limit=${limit}`, null,
    { timeout: 120000 }).then(r => r.data);
export const startHotCinemaVerify = (startDate, endDate, dryRun = true) =>
  api.post(`/admin/hot-cinema-verify?start_date=${startDate}&end_date=${endDate}`
    + `&dry_run=${dryRun}`, null, { timeout: 30000 }).then(r => r.data);
export const fetchHotCinemaVerifyStatus = () =>
  api.get('/admin/hot-cinema-verify/status', { timeout: 20000 }).then(r => r.data);
export const revertScreeningRepoints = (dryRun = true) =>
  api.post(`/admin/screening-repoints/revert?dry_run=${dryRun}`, null,
    { timeout: 300000 }).then(r => r.data);
